import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  Button,
  Stack,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Description,
  OpenInNew,
  Visibility,
  ErrorOutline,
} from '@mui/icons-material';
import { BatchSummary } from '../../services/api';
import { getStatusConfig, formatRelativeTime } from '../../utils/transformationHelpers';
import StatusStepper from './StatusStepper';

interface TransformationCardProps {
  batch: BatchSummary;
  onBootstrapFromProject?: (batchId: string) => void;
}

function getBatchDisplayName(batch: BatchSummary): string {
  if (batch.batch_name) {
    return batch.batch_name;
  }
  if (batch.client_id && batch.client_id !== batch.batch_id) {
    return batch.client_id;
  }
  return `Transformation ${batch.batch_id.substring(0, 8)}`;
}

const TransformationCard: React.FC<TransformationCardProps> = ({ batch, onBootstrapFromProject }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const config = getStatusConfig(batch.status);

  const handlePrimaryAction = () => {
    switch (batch.status) {
      case 'mapped':
      case 'reviewing':
        navigate(`/batches/${batch.batch_id}/workspace`);
        break;
      case 'completed':
        navigate(`/batches/${batch.batch_id}`);
        break;
      case 'failed':
      case 'error':
        navigate(`/batches/${batch.batch_id}`);
        break;
      default:
        navigate(`/batches/${batch.batch_id}`);
    }
  };

  const getPrimaryActionLabel = (): string => {
    switch (batch.status) {
      case 'mapped':
      case 'reviewing':
        return 'Review Mapping';
      case 'completed':
        return 'View Results';
      case 'failed':
      case 'error':
        return 'View Details';
      default:
        return 'View Progress';
    }
  };

  const getPrimaryActionIcon = () => {
    switch (batch.status) {
      case 'mapped':
      case 'reviewing':
        return <Visibility fontSize="small" />;
      case 'failed':
      case 'error':
        return <ErrorOutline fontSize="small" />;
      default:
        return <OpenInNew fontSize="small" />;
    }
  };

  return (
    <Card
      variant="outlined"
      sx={{
        transition: 'all 0.2s ease',
        '&:hover': {
          borderColor: 'primary.main',
          boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.1)}`,
          transform: 'translateY(-1px)',
        },
      }}
    >
      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
        {/* Header row */}
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1,
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Description sx={{ fontSize: 18, color: 'primary.main' }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle2" fontWeight={600} noWrap>
                {getBatchDisplayName(batch)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatRelativeTime(batch.created_at) || 'Just now'}
              </Typography>
            </Box>
          </Stack>
          <Chip
            label={config.label}
            color={config.color}
            size="small"
            sx={{ fontWeight: 500, flexShrink: 0 }}
          />
        </Box>

        {/* Status stepper */}
        <Box sx={{ mb: 2 }}>
          <StatusStepper status={batch.status} compact />
        </Box>

        {/* Row stats */}
        {(batch.total_rows != null || batch.processed_rows != null) && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            {batch.processed_rows != null && batch.total_rows != null
              ? `${batch.processed_rows.toLocaleString()} of ${batch.total_rows.toLocaleString()} rows processed`
              : batch.total_rows != null
                ? `${batch.total_rows.toLocaleString()} rows`
                : ''}
          </Typography>
        )}

        {/* Actions */}
        <Stack direction="row" spacing={1}>
          {batch.status !== 'ready_for_mapping' && (
            <Button
              variant={config.needsAttention ? 'contained' : 'outlined'}
              size="small"
              startIcon={getPrimaryActionIcon()}
              onClick={handlePrimaryAction}
            >
              {getPrimaryActionLabel()}
            </Button>
          )}
          {batch.status === 'ready_for_mapping' && onBootstrapFromProject && (
            <Button
              variant="text"
              size="small"
              onClick={() => onBootstrapFromProject(batch.batch_id)}
              sx={{ textTransform: 'none', fontSize: '0.75rem', color: 'text.secondary' }}
            >
              Bootstrap from Project
            </Button>
          )}
          {batch.status !== 'completed' && batch.status !== 'failed' && batch.status !== 'error' && (
            <Button
              variant="text"
              size="small"
              onClick={() => navigate(`/batches/${batch.batch_id}`)}
            >
              Details
            </Button>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default TransformationCard;
